import React from "react";
import type { FunctionComponent, HTMLAttributes } from "react";
import { Box, Button, Heading, Text } from "theme-ui";
import { motion } from "framer-motion";
import LazyLoad from "react-lazyload";
import { AlgoliaBottle } from "src/carlsberg-headless/types/modules/digital-memories";
import { useSiteContext } from "../../context/site/useSiteContext";
import { Image } from "@shared-components/Image/Image";

export type CardProps = AlgoliaBottle & {
  largeCard?: boolean;
  index: number;
  onClick?: () => void;
};

export const Card: FunctionComponent<
  HTMLAttributes<HTMLAnchorElement> & CardProps
> = ({
  brewery,
  brand,
  name,
  year,
  country,
  countryDk,
  id,
  onClick,
  // query,
}) => {
  const { currentCulture } = useSiteContext();
  const variants = {
    hidden: { opacity: 0, y: 50, ease: "easing" },
    show: {
      opacity: 1,
      y: 0,
      ease: "easing",
    },
  };

  const transition = {
    duration: 0.5,
  };
  const thumbImage = `https://res.cloudinary.com/bottlecollection/image/fetch/w_400,c_scale,a_ignore/https://storage.googleapis.com/cb-bottle-collection.appspot.com/bottles/${id}/1.JPG`;

  return (
    <Box sx={{ maxWidth: "100%" }}>
      {/* <LazyLoad once offset={-150}> */}
      <motion.div
        variants={variants}
        initial={"hidden"}
        animate={"show"}
        transition={transition}
        onClick={onClick}
        className={"card"}
      >
        <Button
          sx={{
            overflow: "hidden",
            borderRadius: "8px",
            color: "white",
            padding: 0,
            textAlign: "start",
            width: "full",
            height: "full",
            cursor: "pointer",
            img: {
              transform: "scale(1)",
              transition: "all 0.2s linear",
            },
            ":hover, :focus": {
              boxShadow: "none",
              img: {
                transform: "scale(1.1)",
                transition: "all 0.2s linear",
              },
            },
          }}
        >
          <Box
            sx={{
              backgroundColor: "white",
              backgroundRepeat: "no-repeat",
              backgroundSize: "contain",
              backgroundPosition: "center",
              width: "100%",
              overflow: "hidden",
              aspectRatio: "272/489",
            }}
          >
            <LazyLoad>
              <Image src={thumbImage} />
            </LazyLoad>
          </Box>
          <Box
            sx={{
              backgroundColor: "darkGreen",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              paddingBlock: "0.75rem",
              paddingInline: "1rem",
              height: "120px",
            }}
          >
            <Heading
              as="h4"
              sx={{
                fontSize: "tinyText",
                textTransform: "uppercase",
                WebkitBoxOrient: "vertical",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                overflow: "hidden",
                my: 0,
                minHeight: "23px",
                letterSpacing: "2px",
              }}
            >
              {brand}
            </Heading>
            <Heading
              as={"h3"}
              sx={{
                fontSize: "h6",
                fontWeight: "700",
                my: 0,
                WebkitBoxOrient: "vertical",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                overflow: "hidden",
                minHeight: "42px",
                letterSpacing: "1px",
              }}
            >
              {name}
            </Heading>
            <Text sx={{ display: [null, "flex"], margin: 0, flexWrap: "wrap" }}>
              {!!year && (
                <Box
                  as={"span"}
                  sx={{ fontSize: "tinyText", marginRight: "0.625rem" }}
                >
                  {year}
                </Box>
              )}
              <Box
                as={"span"}
                sx={{
                  fontSize: "tinyText",
                  // marginRight: "2.5rem",
                  color: "copper",
                  textTransform: "uppercase",
                  letterSpacing: "2px",
                }}
              >
                {currentCulture === "en-us" ? country : countryDk}
              </Box>
            </Text>
          </Box>
        </Button>
      </motion.div>
      {/* </LazyLoad> */}
    </Box>
  );
};
